"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CircleChevronDown,
  CircleChevronUp,
} from "lucide-react";
import rehypeRaw from "rehype-raw";
import { extractPostAuthors } from "@/app/resources/authors/lib/authors";
import { getImageUrl } from "@/app/lib/strapi";
import getConfig from "@/app/utils/config";
import { AuthorLinks } from "@/app/resources/authors/components/AuthorNames";
import { ROUTES } from "@/lib/routes";
import GetStartedButton from "@/app/components/GetStartedButton";
import {
  EXCLUSIVE_PANEL_IDS,
  useExclusivePanelToggle,
} from "@/app/lib/ExclusivePanelContext";

const SCROLL_OFFSET = 96;
const MOBILE_SCROLL_CENTER_HEADER_OFFSET = 160;

type TocItem = { id: string; text: string; level: number };

const slugifyHeading = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

function MarkdownContent({ content }: { content: string }) {
  if (!content) return null;

  const processHtmlContent = (htmlContent: string) => {
    let processed = htmlContent;

    processed = processed.replace(
      /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*?>(.*?)<\/a>/gi,
      (_match, _quote, url, text) => {
        const cleanText = text.replace(/<[^>]*>/g, "").trim();
        return `[${cleanText}](${url})`;
      },
    );

    processed = processed.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      "",
    );
    processed = processed.replace(
      /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi,
      "",
    );
    processed = processed.replace(/<iframe\b[^>]*>.*?<\/iframe>/gi, "");

    processed = processed.replace(/<(?!\/?(strong|em|b|i|u|br)\b)[^>]+>/gi, "");

    processed = processed
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'");

    processed = processed.replace(
      /(?<!\]\()(?<!\()(https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*))/gi,
      (url) => `[${url}](${url})`,
    );

    return processed;
  };

  const sanitizedContent = processHtmlContent(content);

  return (
    <div className="blog-content text-[#FCFAFA]/90">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: (props) => (
            <p {...props} className="mb-5 leading-8 text-[#FCFAFA]/90" />
          ),
          h1: (props) => (
            <h1
              {...props}
              className="text-3xl font-bold mt-12 mb-5 text-[#FCFAFA] scroll-mt-24"
            />
          ),
          h2: (props) => (
            <h2
              {...props}
              className="text-2xl font-bold mt-10 mb-4 text-[#FCFAFA] scroll-mt-24"
            />
          ),
          h3: (props) => (
            <h3
              {...props}
              className="text-xl font-bold mt-8 mb-4 text-[#FCFAFA] scroll-mt-24"
            />
          ),
          ul: (props) => (
            <ul {...props} className="list-disc ml-6 mb-5 text-[#FCFAFA]/90" />
          ),
          ol: (props) => (
            <ol
              {...props}
              className="list-decimal ml-6 mb-5 text-[#FCFAFA]/90"
            />
          ),
          li: (props) => <li {...props} className="mb-1 text-[#FCFAFA]/90" />,
          a: ({ href, children, ...props }) => {
            const safeHref =
              href && !/^https?:\/\//i.test(href) ? `https://${href}` : href;
            return (
              <a
                href={safeHref}
                className="text-[#767EF6] hover:underline cursor-pointer"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (safeHref) e.stopPropagation();
                }}
                {...props}
              >
                {children}
              </a>
            );
          },
          blockquote: (props) => (
            <blockquote
              {...props}
              className="border-l-4 border-[#FCFAFA]/30 pl-4 italic my-4 text-[#FCFAFA]/80"
            />
          ),
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || "");
            const isInline =
              !match &&
              (props as { node?: { parent?: { tagName?: string } } }).node
                ?.parent?.tagName !== "pre";

            if (isInline) {
              return (
                <code
                  {...props}
                  className="bg-gray-800 px-2 py-1 rounded text-sm font-mono"
                >
                  {children}
                </code>
              );
            }

            return (
              <div className="my-4 rounded-lg overflow-hidden border border-gray-600">
                <div className="bg-gray-900 px-3 py-2 text-xs text-gray-400 border-b border-gray-600">
                  {match ? match[1] : "code"}
                </div>
                <code
                  {...props}
                  className="block bg-gray-800 p-4 overflow-x-auto text-sm font-mono text-green-400 whitespace-pre"
                >
                  {children}
                </code>
              </div>
            );
          },
          pre: (props) => <pre {...props} className="my-4" />,
          strong: (props) => <strong {...props} className="font-bold" />,
          em: (props) => <em {...props} className="italic" />,
          img: ({ src, alt }) => {
            if (!src) return null;
            return (
              <img
                src={src}
                alt={alt || ""}
                className="w-full max-w-full h-auto rounded-xl my-8 object-contain"
                loading="lazy"
                width={800}
                height={450}
              />
            );
          },
        }}
      >
        {sanitizedContent}
      </ReactMarkdown>
    </div>
  );
}

/** FAQ answers may mix markdown and inline emphasis from Strapi. */
const FAQAnswerContent: React.FC<{ content: string }> = ({ content }) => {
  if (!content) return null;

  const processContent = (htmlContent: string) => {
    let processed = htmlContent;

    processed = processed.replace(
      /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*?>(.*?)<\/a>/gi,
      (_match, _quote, url, text) => {
        const cleanText = text.replace(/<[^>]*>/g, "").trim();
        return `[${cleanText}](${url})`;
      },
    );

    processed = processed.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      "",
    );
    processed = processed.replace(
      /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi,
      "",
    );
    processed = processed.replace(/<iframe\b[^>]*>.*?<\/iframe>/gi, "");

    processed = processed
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'");

    return processed;
  };

  const processedContent = processContent(content);
  const isUrl = /^https?:\/\//i.test(content.trim());
  const markdownContent = isUrl ? `[${content}](${content})` : processedContent;

  return (
    <div className="text-[#F5F5F5]/90 leading-relaxed text-sm sm:text-base font-semibold">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          p: (props) => (
            <p
              {...props}
              className="mb-3 text-[#F5F5F5]/90 font-semibold last:mb-0"
            />
          ),
          ul: (props) => (
            <ul
              {...props}
              className="ml-6 list-disc mb-3 text-[#F5F5F5]/90 font-semibold space-y-1"
            />
          ),
          ol: (props) => (
            <ol
              {...props}
              className="ml-6 list-decimal mb-3 text-[#F5F5F5]/90 font-semibold space-y-1"
            />
          ),
          li: (props) => (
            <li
              {...props}
              className="mb-1 text-[#F5F5F5]/90 font-semibold"
            />
          ),
          strong: (props) => (
            <strong {...props} className="font-semibold text-[#F5F5F5]" />
          ),
          em: (props) => <em {...props} className="italic text-[#F5F5F5]/90" />,
          a: ({ href, children, ...props }) => {
            if (!href) return <span {...props}>{children}</span>;

            const isInternalLink =
              href.startsWith("/") ||
              (!href.startsWith("http://") &&
                !href.startsWith("https://") &&
                !href.startsWith("mailto:") &&
                !href.startsWith("#"));

            if (isInternalLink) {
              const route = href.startsWith("/") ? href : `/${href}`;
              return (
                <Link
                  href={route}
                  className="text-[#767EF6] hover:text-[#5A63D4] hover:underline transition-colors"
                  {...props}
                >
                  {children}
                </Link>
              );
            }

            const safeHref =
              href && !/^https?:\/\//i.test(href) ? `https://${href}` : href;

            return (
              <a
                href={safeHref}
                className="text-[#767EF6] hover:text-[#5A63D4] hover:underline transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                {children}
              </a>
            );
          },
          code: ({ className, children, ...props }) => {
            const isInline = !className || !className.includes("language-");

            if (isInline) {
              return (
                <code
                  {...props}
                  className="bg-[#2D2C2C] px-2 py-1 rounded text-xs sm:text-sm font-mono text-[#F5F5F5] break-words"
                >
                  {children}
                </code>
              );
            }

            return (
              <code
                {...props}
                className="block bg-[#2D2C2C] p-3 sm:p-4 rounded text-xs sm:text-sm font-mono text-[#F5F5F5] overflow-x-auto my-3"
              >
                {children}
              </code>
            );
          },
          blockquote: (props) => (
            <blockquote
              {...props}
              className="border-l-4 border-[#767EF6]/50 pl-3 sm:pl-4 italic my-3 text-[#F5F5F5]/80"
            />
          ),
        }}
      >
        {markdownContent}
      </ReactMarkdown>
    </div>
  );
};

function normalizeFaqFromAttributes(
  attributes: Record<string, string | unknown>,
): Array<{
  id: number | string;
  Question: string;
  Answer: string;
}> {
  const raw = attributes.FAQ ?? attributes.faq;
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (
    raw &&
    typeof raw === "object" &&
    "data" in raw &&
    Array.isArray((raw as { data: unknown[] }).data)
  ) {
    list = (raw as { data: unknown[] }).data;
  }
  return list
    .map((item, index) => {
      const row = item as Record<string, unknown>;
      const question = String(row.Question || row.question || "");
      const answer = String(row.Answer || row.answer || "");
      const idRaw = row.id ?? index;
      const id =
        typeof idRaw === "string" || typeof idRaw === "number"
          ? idRaw
          : index;
      return { id, Question: question, Answer: answer };
    })
    .filter((item) => item.Question && item.Answer);
}

type ArticlePost = {
  id: number | string;
  attributes: Record<string, string | unknown>;
};

export default function LearnInDepthArticleClient({ post }: { post: ArticlePost }) {
  const { attributes } = post;
  const postAuthors = extractPostAuthors(post);
  const title = String(attributes.title || "Untitled guide");
  const [openFAQItems, setOpenFAQItems] = useState<number[]>([]);
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeHeadingId, setActiveHeadingId] = useState<string>("");
  const {
    isOpen: isTocOpenMobile,
    toggle: toggleTocMobile,
    close: closeTocMobile,
  } = useExclusivePanelToggle(EXCLUSIVE_PANEL_IDS.LEARN_IN_DEPTH_TOC_MOBILE);
  const contentRef = useRef<HTMLDivElement>(null);
  const isClickingRef = useRef(false);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deferTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tocListRef = useRef<HTMLUListElement>(null);
  const router = useRouter();

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      if (deferTimeoutRef.current) clearTimeout(deferTimeoutRef.current);
    };
  }, []);

  // Auto-scroll the TOC sidebar to keep the active item visible
  useEffect(() => {
    if (!activeHeadingId || !tocListRef.current) return;
    const activeLink = tocListRef.current.querySelector<HTMLElement>(
      `[href="#${CSS.escape(activeHeadingId)}"]`,
    );
    if (activeLink) {
      activeLink.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeHeadingId]);

  const markdownSourceSignature = useMemo(() => {
    const parts = [
      attributes.summaryBox,
      attributes.summeryBox,
      attributes.summary,
      attributes.description,
      attributes.description2,
      attributes.description3,
      attributes.description4,
      attributes.description5,
    ].map((v) => (typeof v === "string" ? v : ""));
    return parts.join("||");
  }, [attributes]);

  useEffect(() => {
    try {
      sendGTMEvent({
        event: "view-learn-in-depth",
        guideTitle: title,
      });
    } catch (error) {
      console.debug("GTM event error:", error);
    }
  }, [title]);

  const calculateReadTime = (text: string) => {
    if (!text) return "2";
    const words = text.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200)).toString();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-US", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      })
      .replace(/\//g, " / ");
  };

  const readTimeSource = [
    attributes.description,
    attributes.description2,
    attributes.description3,
    attributes.description4,
    attributes.description5,
  ]
    .filter((v): v is string => typeof v === "string")
    .join("\n");

  const faqList = useMemo(
    () => normalizeFaqFromAttributes(attributes),
    [attributes],
  );

  const toggleFAQItem = (index: number) => {
    setOpenFAQItems((prev) => (prev.includes(index) ? [] : [index]));
  };

  const assignHeadingIds = useCallback((shouldUpdateToc: boolean) => {
    if (!contentRef.current || typeof window === "undefined")
      return [] as TocItem[];

    const headings = Array.from(
      contentRef.current.querySelectorAll<HTMLElement>(
        "h1, h2, h3, h4, h5, h6",
      ),
    );

    const idCounter = new Map<string, number>();
    const nextTocItems: TocItem[] = headings.map((heading) => {
      const text = heading.textContent?.trim() || "";
      const level = Number(heading.tagName.slice(1)) || 2;
      const baseId = slugifyHeading(text || "section");
      const count = idCounter.get(baseId) || 0;
      const finalId = count === 0 ? baseId : `${baseId}-${count + 1}`;

      idCounter.set(baseId, count + 1);
      heading.id = finalId;

      return { id: finalId, text: text || "Untitled section", level };
    });

    if (shouldUpdateToc) {
      setTocItems(nextTocItems);
    }

    return nextTocItems;
  }, []);

  const scrollToHeading = useCallback(
    (id: string) => {
      if (typeof window === "undefined") return;

      setActiveHeadingId(id);
      isClickingRef.current = true;
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = setTimeout(() => {
        isClickingRef.current = false;
      }, 1000); // 1 s is enough for smooth scroll to finish

      // Defer the scroll by one event loop tick to guarantee React flushes the 
      // state update and the browser paints the highlight instantly BEFORE scrolling begins.
      if (deferTimeoutRef.current) clearTimeout(deferTimeoutRef.current);
      deferTimeoutRef.current = setTimeout(() => {
        assignHeadingIds(false);
        let attempts = 0;

        const attemptScroll = () => {
          const target =
            document.getElementById(id) ||
            document.querySelector<HTMLElement>(
              `[id="${id.replace(/"/g, '\\"')}"]`,
            );

          if (!target) {
            attempts += 1;
            if (attempts <= 8) {
              window.requestAnimationFrame(attemptScroll);
            } else {
              window.location.hash = id;
            }
            return;
          }

          const absoluteTop =
            target.getBoundingClientRect().top +
            window.pageYOffset -
            SCROLL_OFFSET;
          const isMobileViewport = window.matchMedia(
            "(max-width: 1023px)",
          ).matches;
          const mobileCenteredTop =
            target.getBoundingClientRect().top +
            window.pageYOffset -
            window.innerHeight / 2 +
            target.offsetHeight / 2 -
            MOBILE_SCROLL_CENTER_HEADER_OFFSET;
          window.history.replaceState(null, "", `#${id}`);
          window.scrollTo({
            top: Math.max(isMobileViewport ? mobileCenteredTop : absoluteTop, 0),
            behavior: "smooth",
          });

          target.setAttribute("tabindex", "-1");
          target.focus({ preventScroll: true });
        };

        attemptScroll();
      }, 0);
    },
    [assignHeadingIds],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const nextTocItems = assignHeadingIds(true);
    if (!nextTocItems.length) {
      setTocItems([]);
      return;
    }

    const hashId = window.location.hash.replace("#", "");
    if (hashId) {
      const hasMatch = nextTocItems.some((item) => item.id === hashId);
      if (hasMatch) {
        setTimeout(() => scrollToHeading(hashId), 50);
      }
    }
  }, [assignHeadingIds, markdownSourceSignature, scrollToHeading]);

  const syncActiveHeadingFromScroll = useCallback(() => {
    if (isClickingRef.current) return;
    if (!contentRef.current || !tocItems.length) return;

    let items = tocItems;
    let headingElements = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (headingElements.length !== items.length) {
      items = assignHeadingIds(false);
      headingElements = items
        .map((item) => document.getElementById(item.id))
        .filter((el): el is HTMLElement => Boolean(el));
    }

    if (!headingElements.length) return;

    /**
     * Active heading = the last heading whose top edge has reached the 3/4
     * mark of the screen (75% of viewport height).
     * Headings are ordered top-to-bottom in document order, so as soon as
     * a heading's top position exceeds the threshold, subsequent headings will also
     * exceed it. Breaking early stops calling getBoundingClientRect unnecessarily.
     */
    const DETECT_THRESHOLD = window.innerHeight * 0.5; // 50% down the viewport (center of screen)
    let activeId = "";
    for (const el of headingElements) {
      if (el.getBoundingClientRect().top <= DETECT_THRESHOLD) {
        activeId = el.id;
      } else {
        break;
      }
    }

    const next = activeId || headingElements[0].id;
    setActiveHeadingId((prev) => (prev === next ? prev : next));
  }, [assignHeadingIds, tocItems]);

  useEffect(() => {
    if (typeof window === "undefined" || !tocItems.length) return;

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        syncActiveHeadingFromScroll();
      });
    };

    schedule();

    const scrollOpts: AddEventListenerOptions = { passive: true };
    const scrollOptsCapture: AddEventListenerOptions = {
      passive: true,
      capture: true,
    };
    window.addEventListener("scroll", schedule, scrollOpts);
    document.addEventListener("scroll", schedule, scrollOptsCapture);
    const scrollingEl =
      document.scrollingElement instanceof HTMLElement
        ? document.scrollingElement
        : null;
    if (scrollingEl) {
      scrollingEl.addEventListener("scroll", schedule, scrollOpts);
    }
    window.visualViewport?.addEventListener("scroll", schedule, scrollOpts);
    window.addEventListener("resize", schedule, scrollOpts);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule, scrollOpts);
      document.removeEventListener("scroll", schedule, scrollOptsCapture);
      if (scrollingEl) {
        scrollingEl.removeEventListener("scroll", schedule, scrollOpts);
      }
      window.visualViewport?.removeEventListener("scroll", schedule, scrollOpts);
      window.removeEventListener("resize", schedule, scrollOpts);
    };
  }, [tocItems, syncActiveHeadingFromScroll]);

  const strapiBase = getConfig().STRAPI_URL || "";
  const desktopImageUrl = getImageUrl(attributes.desktopView, strapiBase);
  const image1Url = getImageUrl(attributes.image1, strapiBase);
  const image2Url = getImageUrl(attributes.image2, strapiBase);

  const summaryHighlight =
    (typeof attributes.summaryBox === "string" && attributes.summaryBox) ||
    (typeof attributes.summeryBox === "string" && attributes.summeryBox) ||
    (typeof attributes.summary === "string" && attributes.summary) ||
    "";

  return (
    <main className="min-h-screen bg-[#191919] text-[#FCFAFA] py-8 md:py-12  px-6 lg:px-8">
      <article className="lg:max-w-[900px] xl:max-w-[1200px] 2xl:max-w-[1600px] mx-auto md:pl-6 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-7 min-w-0">
            <motion.h1
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6 text-[#FCFAFA] leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {title}
            </motion.h1>

            <motion.div
              className="mb-8 text-[18px] text-[#FCFAFA] leading-[2]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <AuthorLinks
                authors={postAuthors}
                label="Author :"
                className="mb-1"
                linkClassName="font-semibold hover:underline transition-all duration-300"
              />
              <p>
                Publish Date :{" "}
                {formatDate(
                  String(
                    attributes.publishedAt ||
                    attributes.createdAt ||
                    new Date().toISOString(),
                  ),
                )}{" "}
                <span className="md:ml-5">
                  • {calculateReadTime(readTimeSource)} mins read
                </span>
              </p>
              <p className="text-[#FCFAFA]/80">
                Last Updated :{" "}
                {formatDate(
                  String(
                    attributes.updatedAt ||
                    attributes.publishedAt ||
                    attributes.createdAt ||
                    new Date().toISOString(),
                  ),
                )}
              </p>
            </motion.div>

            {desktopImageUrl ? (
              <motion.div
                className="w-full max-w-full mx-auto mb-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="relative w-full max-w-full aspect-[16/9] min-h-[200px] md:min-h-[280px] overflow-hidden rounded-lg ">
                  <Image
                    src={desktopImageUrl}
                    alt={String(attributes.desktopViewAlt || title)}
                    fill
                    sizes="(max-width: 768px) 100vw, 100%"
                    className="object-contain"
                    style={{ objectFit: "contain" }}
                    priority
                  />
                </div>
              </motion.div>
            ) : null}

            <div className="bg-transparent">
              <div ref={contentRef} className="min-w-0">
                {tocItems.length > 0 && (
                  <div className="lg:hidden mb-6">
                    <button
                      type="button"
                      onClick={toggleTocMobile}
                      className="w-full rounded-lg border border-[#FCFAFA]/20 bg-[#232323] px-4 py-3 text-left text-sm font-semibold text-[#FCFAFA] hover:border-[#767EF6]/60 focus:outline-none focus:ring-2 focus:ring-[#767EF6]"
                      aria-expanded={isTocOpenMobile}
                      aria-controls="mobile-learn-in-depth-toc"
                    >
                      Table of Contents
                    </button>
                    {isTocOpenMobile && (
                      <nav
                        id="mobile-learn-in-depth-toc"
                        aria-label="Guide table of contents mobile"
                        className="mt-3 rounded-lg border border-[#FCFAFA]/10 bg-[#232323] p-3"
                      >
                        <ul className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
                          {tocItems.map((item) => {
                            const isActive = activeHeadingId === item.id;
                            return (
                              <li key={`mobile-${item.id}`}>
                                <a
                                  href={`#${item.id}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    scrollToHeading(item.id);
                                    closeTocMobile();
                                  }}
                                  className={`block w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${isActive
                                    ? "text-[#323DD3]"
                                    : "text-[#FCFAFA]/80 hover:text-[#FCFAFA]"
                                    }`}
                                  style={{
                                    paddingLeft: `${Math.max(
                                      8,
                                      (item.level - 1) * 12,
                                    )}px`,
                                  }}
                                >
                                  <span className="block whitespace-normal break-words">
                                    {item.text}
                                  </span>
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </nav>
                    )}
                  </div>
                )}

                {summaryHighlight ? (
                  <motion.div
                    className="mb-8"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <div className="bg-[#232323] border border-[#FCFAFA]/10 rounded-xl p-6">
                      <h3 className="text-[#FCFAFA] font-semibold mb-3 text-xl">
                        Key Highlights
                      </h3>
                      <div className="text-[#FCFAFA]/80">
                        <MarkdownContent content={summaryHighlight} />
                      </div>
                    </div>
                  </motion.div>
                ) : null}

                {typeof attributes.description === "string" &&
                  attributes.description && (
                    <motion.div
                      className="mb-8"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                    >
                      <MarkdownContent content={attributes.description} />
                    </motion.div>
                  )}

                {image1Url ? (
                  <motion.div
                    className="w-full mb-10"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    <div className="relative w-full h-[260px] sm:h-[360px] md:h-[500px] rounded-2xl overflow-hidden">
                      <Image
                        src={image1Url}
                        alt={String(
                          attributes.image1Alt || `${title} - Image 1`,
                        )}
                        fill
                        sizes="(max-width: 1024px) 100vw, 80vw"
                        className="object-contain"
                        style={{ objectFit: "contain" }}
                      />
                    </div>
                  </motion.div>
                ) : null}

                {typeof attributes.description2 === "string" &&
                  attributes.description2 && (
                    <motion.div
                      className="mb-8"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.7 }}
                    >
                      <MarkdownContent content={attributes.description2} />
                    </motion.div>
                  )}

                {image2Url ? (
                  <motion.div
                    className="w-full mb-10"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                  >
                    <div className="relative w-full h-[260px] sm:h-[360px] md:h-[500px] rounded-2xl overflow-hidden">
                      <Image
                        src={image2Url}
                        alt={String(
                          attributes.image2Alt || `${title} - Image 2`,
                        )}
                        fill
                        sizes="(max-width: 1024px) 100vw, 80vw"
                        className="object-contain"
                        style={{ objectFit: "contain" }}
                      />
                    </div>
                  </motion.div>
                ) : null}

                {typeof attributes.description3 === "string" &&
                  attributes.description3 && (
                    <motion.div
                      className="mb-8"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.9 }}
                    >
                      <MarkdownContent content={attributes.description3} />
                    </motion.div>
                  )}

                {typeof attributes.description4 === "string" &&
                  attributes.description4 && (
                    <motion.div
                      className="mb-8"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 1.0 }}
                    >
                      <MarkdownContent content={attributes.description4} />
                    </motion.div>
                  )}

                {typeof attributes.description5 === "string" &&
                  attributes.description5 && (
                    <motion.div
                      className="mb-8"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 1.05 }}
                    >
                      <MarkdownContent content={attributes.description5} />
                    </motion.div>
                  )}
              </div>
            </div>

            {faqList.length > 0 && (
              <motion.section
                className="mt-20"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-[#767EF6]">
                  FAQ
                </h2>
                <div className="space-y-3 sm:space-y-4">
                  {faqList.map((item, index) => {
                    const isOpen = openFAQItems.includes(index);
                    const faqId = `learn-in-depth-faq-${item.id ?? index}`;
                    const answerId = `learn-in-depth-answer-${item.id ?? index}`;
                    return (
                      <div key={String(item.id ?? index)} className="space-y-0">
                        <button
                          type="button"
                          id={faqId}
                          onClick={() => toggleFAQItem(index)}
                          aria-expanded={isOpen}
                          aria-controls={answerId}
                          className="w-full rounded-[27px] border-2 border-[#615D5D] px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:border-[#767EF6]/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#767EF6] focus:ring-inset"
                        >
                          <span className="text-base sm:text-lg font-semibold text-white pr-3 sm:pr-4 flex-1 text-left">
                            {item.Question}
                          </span>
                          <div className="flex-shrink-0" aria-hidden="true">
                            {isOpen ? (
                              <CircleChevronUp className="h-5 w-5 sm:h-6 sm:w-6 text-[#000000] bg-[#767EF6] rounded-full" />
                            ) : (
                              <CircleChevronDown className="h-5 w-5 sm:h-6 sm:w-6 text-[#000000] bg-[#767EF6] rounded-full" />
                            )}
                          </div>
                        </button>
                        <div
                          id={answerId}
                          className={`pt-3 pb-2 px-4 sm:px-6 ${isOpen ? "block" : "hidden"}`}
                          role="region"
                          aria-labelledby={faqId}
                          aria-hidden={!isOpen}
                        >
                          <FAQAnswerContent content={item.Answer} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.section>
            )}
          </div>

          <aside className="hidden lg:block lg:col-span-3 self-start sticky top-24">
            {tocItems.length > 0 && (
              <>
                <nav
                  aria-label="Guide table of contents"
                  className="rounded-xl border border-[#FCFAFA]/10 bg-[#232323]/80 p-4 backdrop-blur-sm"
                >
                  <p className="text-xs uppercase tracking-wide text-[#FCFAFA]/60 mb-3">
                    Table of contents
                  </p>
                  <ul ref={tocListRef} className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
                    {tocItems.map((item) => {
                      const isActive = activeHeadingId === item.id;
                      return (
                        <li key={item.id}>
                          <a
                            href={`#${item.id}`}
                            aria-current={isActive ? "location" : undefined}
                            onClick={(e) => {
                              e.preventDefault();
                              scrollToHeading(item.id);
                            }}
                            className={`block w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${isActive ? "text-[#323DD3]" : "text-[#FCFAFA]/75 hover:text-[#FCFAFA]"
                              }`}
                            style={{
                              paddingLeft: `${Math.max(
                                8,
                                (item.level - 1) * 12,
                              )}px`,
                            }}
                          >
                            <span className="block whitespace-normal break-words">
                              {item.text}
                            </span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </nav>

                <div className="mt-4 rounded-2xl max-w-[300px] mx-auto border border-[#FCFAFA]/10 bg-[#232323]/80 p-5 text-center ">
                  <p className="text-[16px] leading-[30px] font-semibold text-[#FCFAFA]">
                    Start Building Your Strategy with PineGen AI Today
                  </p>
                  <GetStartedButton
                    className="mt-5 inline-flex items-center justify-center gap-2 !rounded-full !bg-[#3541ED] !px-6 !py-2 !text-sm font-semibold text-white transition-colors hover:!bg-[#2E38D0]"
                  >
                    <span>Get Started</span>
                    <ArrowRight className="h-4 w-4" />
                  </GetStartedButton>
                </div>
              </>
            )}
          </aside>
        </div>
      </article>
    </main>
  );
}
